import { formatDisplayTitle } from '../formatProductTitle';
import { productMatchesAnyGiftTag } from '../productGiftTags';

export const MAX_GIFT_GROUPS = 12;
export const MAX_GIFT_RECIPIENTS = 8;

export function parseSlugList(raw) {
  if (Array.isArray(raw)) {
    return raw.map((s) => String(s || '').trim().toLowerCase()).filter((s) => s && s !== 'all');
  }
  return String(raw || '')
    .split(/[,;\n]+/)
    .map((s) => s.trim().toLowerCase())
    .filter((s) => s && s !== 'all');
}

export function slugifyGiftId(raw, fallback = 'item') {
  const s = String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return s || fallback;
}

export function usesAdminGiftGroups(config) {
  return (
    config?.use_admin_gift_groups !== false &&
    Array.isArray(config?.gift_groups) &&
    config.gift_groups.length > 0
  );
}

export function findAdminGiftGroup(config, groupId) {
  const g = String(groupId || '').trim().toLowerCase();
  if (!g) return null;
  return (config?.gift_groups || []).find((x) => String(x.id || '').trim().toLowerCase() === g) || null;
}

/** @param {object} product @param {object} groupDef */
export function productMatchesAdminGiftGroup(product, groupDef) {
  if (!groupDef) return false;
  const cats = groupDef.categories || [];
  const pc = String(product?.category || '').trim().toLowerCase();
  if (cats.length > 0 && !cats.includes(pc)) return false;

  const types = groupDef.types || [];
  const subs = groupDef.subcategories || [];
  const tags = groupDef.tags || [];
  if (types.length === 0 && subs.length === 0 && tags.length === 0) return true;

  const typ = String(product?.type || '').trim().toLowerCase();
  const sub = String(product?.subcategory || '').trim().toLowerCase();
  const tagHit = tags.length > 0 && productMatchesAnyGiftTag(product, tags);
  return (
    (types.length > 0 && types.includes(typ)) ||
    (subs.length > 0 && subs.includes(sub)) ||
    tagHit
  );
}

export function productMatchesGroupFilter(product, groupKey, config) {
  const g = String(groupKey || '').trim().toLowerCase();
  if (!g || g === 'any') return true;
  if (usesAdminGiftGroups(config)) {
    const def = findAdminGiftGroup(config, g);
    if (def) return productMatchesAdminGiftGroup(product, def);
  }
  const typ = String(product.type || '').trim().toLowerCase();
  const sub = String(product.subcategory || '').trim().toLowerCase();
  if (typ === g || sub === g) return true;
  return productMatchesAnyGiftTag(product, [g]);
}

export function resolveRecipient(config, recipientId) {
  const id = String(recipientId || '').trim().toLowerCase();
  if (!id || id === 'any') return null;
  const rec = (config?.recipients || []).find((r) => String(r.id || '').trim().toLowerCase() === id);
  if (!rec) return null;
  return {
    id,
    label: rec.label || formatDisplayTitle(id),
    category: String(rec.category || 'any').trim().toLowerCase() || 'any',
    group: String(rec.group || '').trim().toLowerCase(),
    group_ids: parseSlugList(rec.group_ids),
  };
}

export function buildAdminGiftGroupOptions(scopedProducts, giftGroups) {
  const options = [
    {
      id: 'any',
      label: 'All types',
      hint: 'Show every design in this collection',
      icon: '✨',
    },
  ];

  for (const def of giftGroups || []) {
    const id = String(def.id || '').trim().toLowerCase();
    if (!id) continue;
    const n = (scopedProducts || []).filter((p) => productMatchesAdminGiftGroup(p, def)).length;
    if (n === 0) continue;
    options.push({
      id,
      label: def.label || formatDisplayTitle(id),
      hint: def.hint || `${n} design${n === 1 ? '' : 's'}`,
      icon: def.icon || '·',
    });
  }

  return options;
}

export function buildRecipientOptions(recipients) {
  const options = [
    {
      id: 'any',
      label: 'Not sure yet',
      hint: 'Browse all collections',
      icon: '🎁',
    },
  ];
  for (const rec of recipients || []) {
    const id = String(rec.id || '').trim().toLowerCase();
    if (!id) continue;
    options.push({
      id,
      label: rec.label || formatDisplayTitle(id),
      hint: rec.hint || '',
      icon: rec.icon || '·',
    });
  }
  return options;
}

/** Relax filters when results are too few (industry-style fallback). */
export function widenGiftFinderFilters({ category, group, budget, recipient }, config) {
  const steps = [];
  if (budget && budget !== 'any') steps.push({ budget: 'any', label: 'any budget' });
  if (group && group !== 'any') steps.push({ group: 'any', label: 'all types' });
  if (recipient && recipient !== 'any') steps.push({ recipient: 'any', label: 'all recipients' });
  if (category && category !== 'any') steps.push({ category: 'any', label: 'all collections' });

  if (!steps.length) return null;

  const applied = { category, group, budget, recipient: recipient || 'any' };
  const first = steps[0];
  if (first.budget !== undefined) applied.budget = 'any';
  else if (first.group !== undefined) applied.group = 'any';
  else if (first.recipient !== undefined) applied.recipient = 'any';
  else if (first.category !== undefined) applied.category = 'any';

  return { filters: applied, relaxedLabel: first.label };
}
